import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, Mail, Facebook } from 'lucide-react';

const XIcon = ({ size = 20 }: { size?: number }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.292 19.494h2.039L6.486 3.24H4.298l13.311 17.407z" />
  </svg>
);

const TikTokIcon = ({ size = 20 }: { size?: number }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const Footer: React.FC = () => {
  return (
    <footer className="relative z-10 bg-[#0A0612]/90 border-t border-white/5 py-12">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div className="col-span-1 md:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="flex flex-col">
                <div className="flex items-baseline gap-1 leading-none">
                  <span className="text-xl font-orbitron font-bold tracking-wider text-white">VEXTORA</span>
                  <span className="text-lg font-orbitron font-light tracking-wider text-gray-400">MEDIA</span>
                </div>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Accelerating digital brands through AI-driven content systems and premium publishing infrastructure.
            </p>
            <div className="flex flex-wrap gap-5">
              <a 
                href="https://x.com/Vextoramedia" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-purple-400 transition-all hover:scale-110"
                aria-label="X (formerly Twitter)"
              >
                <XIcon size={18} />
              </a>
              <a 
                href="https://www.instagram.com/vextoramedia" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-pink-500 transition-all hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.youtube.com/@VextoraMedia" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-red-500 transition-all hover:scale-110"
                aria-label="YouTube"
              >
                <Youtube size={20} />
              </a>
              <a 
                href="https://www.facebook.com/vextoramedia" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-blue-500 transition-all hover:scale-110"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="https://www.tiktok.com/@vextora.media" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-400 hover:text-cyan-400 transition-all hover:scale-110"
                aria-label="TikTok"
              >
                <TikTokIcon size={20} />
              </a>
              <a 
                href="mailto:hello@vextora.media" 
                className="text-gray-400 hover:text-purple-400 transition-all hover:scale-110"
                aria-label="Email"
              >
                <Mail size={20} />
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="text-white font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-4">
              <li><Link to="/" className="text-gray-400 hover:text-purple-400 text-sm transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-gray-400 hover:text-purple-400 text-sm transition-colors">About Us</Link></li>
              <li><Link to="/blog" className="text-gray-400 hover:text-purple-400 text-sm transition-colors">Insights</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-purple-400 text-sm transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-6">Services</h3>
            <ul className="space-y-4">
              <li><Link to="/services" className="text-gray-400 hover:text-purple-400 text-sm transition-colors">AI Publishing</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-purple-400 text-sm transition-colors">KDP Brand Creation</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-purple-400 text-sm transition-colors">Media Production</Link></li>
              <li><Link to="/services" className="text-gray-400 hover:text-purple-400 text-sm transition-colors">Brand Strategy</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-6">Contact Us</h3>
            <p className="text-gray-400 text-sm mb-4">Direct inquiries to our team:</p>
            <a 
              href="mailto:hello@vextora.media" 
              className="flex items-center gap-2 text-purple-400 hover:text-purple-300 font-medium text-sm transition-colors"
            >
              <Mail size={16} />
              hello@vextora.media
            </a>
          </div>
        </div>
        
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-xs">
            © {new Date().getFullYear()} Vextora Media. All rights reserved.
          </p>
          <div className="flex gap-8">
            <Link to="/privacy" className="text-gray-400 hover:text-white text-xs transition-colors">Privacy Policy</Link>
            <Link to="/cookies" className="text-gray-400 hover:text-white text-xs transition-colors">Cookie Policy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white text-xs transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;